import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import NoticeCard from '../../components/support/NoticeCard';
import ErrorState from '../../components/common/ErrorState';
import { ListSkeleton } from '../../components/common/Skeleton';
import { useToast } from '../../context/ToastContext';
import { fetchNotices } from '../../utils/api';

const PAGE_SIZE = 5;

/**
 * NoticeListPage - 공지사항 목록 페이지
 */
const NoticeListPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [notices, setNotices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [sortBy, setSortBy] = useState('latest'); // latest | views
  const [page, setPage] = useState(1);

  const loadNotices = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsError(false);
      const data = await fetchNotices();
      if (!data || data.length === 0) {
        setNotices([]);
      } else {
        setNotices(data);
      }
    } catch (error) {
      console.error('Failed to load notices:', error);
      setIsError(true);
      showToast('공지사항을 불러오는데 실패했습니다.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadNotices();
  }, [loadNotices]);

  // 검색어 디바운스 (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // 필터/검색/카테고리 변경 시 첫 페이지로
  useEffect(() => {
    setPage(1);
  }, [debouncedTerm, selectedCategory, sortBy]);

  // 데이터에서 카테고리 동적 추출 + 카운트
  const categoryCounts = useMemo(() => {
    const counts = { 전체: notices.length };
    notices.forEach((n) => {
      counts[n.category] = (counts[n.category] || 0) + 1;
    });
    return counts;
  }, [notices]);

  const categories = useMemo(
    () => ['전체', ...Object.keys(categoryCounts).filter((c) => c !== '전체')],
    [categoryCounts]
  );

  // 필터링 + 정렬 (중요 공지 상단 고정)
  const filteredNotices = useMemo(() => {
    let filtered = [...notices];

    if (selectedCategory !== '전체') {
      filtered = filtered.filter((n) => n.category === selectedCategory);
    }

    if (debouncedTerm.trim()) {
      const term = debouncedTerm.toLowerCase();
      filtered = filtered.filter(
        (n) =>
          n.title.toLowerCase().includes(term) ||
          n.content.toLowerCase().includes(term)
      );
    }

    const compare = (a, b) => {
      // 중요 공지 우선 고정
      if (a.isImportant !== b.isImportant) return a.isImportant ? -1 : 1;
      if (sortBy === 'views') return b.views - a.views;
      return new Date(b.createdAt) - new Date(a.createdAt);
    };

    return filtered.sort(compare);
  }, [notices, selectedCategory, debouncedTerm, sortBy]);

  // 페이지네이션
  const totalPages = Math.max(1, Math.ceil(filteredNotices.length / PAGE_SIZE));
  const pagedNotices = filteredNotices.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const handleNoticeClick = (noticeId) => {
    navigate(`/support/notices/${noticeId}`);
  };

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* 헤더 */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-primary-900 mb-2">공지사항</h1>
          <p className="text-sm text-accent-600">
            서비스 관련 주요 공지사항을 확인하세요
          </p>
        </div>

        {/* 카테고리 필터 (카운트 포함) */}
        <div className="mb-4 flex gap-2 flex-wrap">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              aria-pressed={selectedCategory === category}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedCategory === category
                  ? 'bg-primary-600 text-white'
                  : 'bg-accent-100 text-accent-700 hover:bg-accent-200'
              }`}
            >
              {category}
              {categoryCounts[category] != null && (
                <span className="ml-1 opacity-80">
                  ({categoryCounts[category]})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* 검색 + 정렬 */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-400"
              size={20}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="공지사항 검색..."
              aria-label="공지사항 검색"
              className="w-full pl-10 pr-10 py-3 rounded-lg border border-accent-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                aria-label="검색어 지우기"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-400 hover:text-primary"
              >
                <X size={18} />
              </button>
            )}
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="정렬 기준"
            className="rounded-lg border border-accent-300 bg-white px-3 py-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="latest">최신순</option>
            <option value="views">조회순</option>
          </select>
        </div>

        {/* 공지사항 목록 */}
        {isLoading ? (
          <ListSkeleton rows={5} />
        ) : isError ? (
          <ErrorState
            title="공지사항을 불러오지 못했습니다"
            message="네트워크 상태를 확인한 후 다시 시도해주세요."
            onRetry={loadNotices}
          />
        ) : filteredNotices.length > 0 ? (
          <>
            <div className="space-y-3" aria-live="polite">
              {pagedNotices.map((notice) => (
                <NoticeCard
                  key={notice.id}
                  notice={notice}
                  searchTerm={debouncedTerm}
                  onClick={() => handleNoticeClick(notice.id)}
                />
              ))}
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-lg px-3 py-2 text-sm text-accent-700 hover:bg-accent-100 disabled:opacity-40"
                >
                  이전
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    aria-current={page === i + 1 ? 'page' : undefined}
                    className={`min-w-[2.25rem] rounded-lg px-3 py-2 text-sm font-medium ${
                      page === i + 1
                        ? 'bg-primary-600 text-white'
                        : 'text-accent-700 hover:bg-accent-100'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="rounded-lg px-3 py-2 text-sm text-accent-700 hover:bg-accent-100 disabled:opacity-40"
                >
                  다음
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12 bg-accent-50 rounded-lg">
            <p className="text-accent-600">
              {debouncedTerm || selectedCategory !== '전체'
                ? '검색 결과가 없습니다.'
                : '등록된 공지사항이 없습니다.'}
            </p>
          </div>
        )}

        {/* 통계 정보 */}
        {!isLoading && !isError && filteredNotices.length > 0 && (
          <div className="mt-6 text-center text-sm text-accent-600">
            총 {filteredNotices.length}개의 공지사항
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default NoticeListPage;
